/**
 * Comandi per una striscia di immagini che scorre in orizzontale.
 *
 * Lo scorrimento resta quello nativo del browser: questo elemento aggiunge
 * soltanto i comandi, cioe' le frecce e i pallini che dicono a chi guarda che
 * c'e' altro di lato. Senza JavaScript la striscia continua a scorrere con
 * dito, rotella e tastiera, solo senza indicatori: e' un miglioramento, non
 * una dipendenza.
 *
 * I comandi stanno SOPRA la fotografia, non sotto: una fila di pallini sotto
 * aggiungerebbe altezza, e la striscia su telefono deve arrivare ai bordi
 * senza fasce vuote.
 */
class MediaScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-scroller-track]');
    if (!this.track) return;

    // Le copie per chi non ha JavaScript stanno nella striscia dentro
    // noscript: non sono diapositive, e contarle aggiungerebbe pallini vuoti.
    this.items = [...this.track.children].filter((el) => el.localName !== 'noscript');
    if (this.items.length < 2) return;

    this.#sveglia();
    this.#costruisci();
    this.#aggiorna();

    // `passive`: lo scorrimento non va bloccato per aggiornare dei pallini.
    this.track.addEventListener('scroll', this.#programma, { passive: true });

    // La striscia puo' smettere di scorrere quando la finestra si allarga:
    // a quel punto i comandi non servono e vanno tolti, non nascosti a meta'.
    this.osservatore = new ResizeObserver(this.#programma);
    this.osservatore.observe(this.track);

    if (this.dataset.auto) this.#avviaAutomatico();
  }

  /**
   * Avanzamento automatico.
   *
   * Tre regole che non sono opzionali.
   *
   * 1. Un comando per fermarlo, visibile e raggiungibile da tastiera. Un
   *    contenuto che si muove da solo per piu' di cinque secondi senza modo
   *    di fermarlo e' una violazione del criterio WCAG 2.2.2, e in pratica
   *    e' un problema per chi legge lentamente: la fotografia cambia a meta'
   *    della frase che ci sta sopra.
   * 2. Chi ha chiesto meno animazioni non lo vede partire affatto.
   * 3. Si ferma mentre qualcuno ci interagisce — dito sopra, fuoco dentro,
   *    scheda in secondo piano — e riparte dopo. Scorrere a mano e vedersi
   *    strappare via la fotografia e' il difetto tipico di questi caroselli.
   */
  #avviaAutomatico() {
    this.riduciMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.intervallo = Math.max(2000, Number(this.dataset.auto) || 4000);
    this.inPausa = false;

    this.bottonePausa = this.#bottone('scroller__pause', this.dataset.labelPause);
    this.bottonePausa.addEventListener('click', () => this.#pausa(!this.inPausa, true));
    this.pallini.before(this.bottonePausa);

    for (const [evento, stato] of [
      ['pointerenter', true], ['pointerleave', false],
      ['focusin', true], ['focusout', false],
      ['pointerdown', true], ['pointerup', false],
    ]) {
      this.addEventListener(evento, () => {
        if (!this.fermatoDaUtente) this.#pausa(stato, false);
      });
    }

    document.addEventListener('visibilitychange', this.#visibilita);

    // Fuori dallo schermo non serve far girare niente.
    if (typeof IntersectionObserver === 'function') {
      this.vista = new IntersectionObserver(
        ([e]) => { if (!this.fermatoDaUtente) this.#pausa(!e.isIntersecting, false); },
        { threshold: 0.2 }
      );
      this.vista.observe(this);
    }

    this.#pausa(this.riduciMovimento.matches, false);
    this.riduciMovimento.addEventListener('change', (e) => this.#pausa(e.matches, false));
  }

  #arrivata(elemento) {
    const foto = elemento.matches('img') ? elemento : elemento.querySelector('img');
    // `complete` e' vero anche per un'immagine senza indirizzo, cioe' per una
    // diapositiva ancora in attesa: conta solo se ha gia' una sorgente.
    return !foto || (foto.complete && Boolean(foto.currentSrc));
  }

  #visibilita = () => {
    if (!this.fermatoDaUtente) this.#pausa(document.hidden, false);
  };

  #pausa(fermo, daUtente) {
    if (daUtente) this.fermatoDaUtente = fermo;
    this.inPausa = fermo;
    this.toggleAttribute('data-in-pausa', fermo);

    if (this.bottonePausa) {
      this.bottonePausa.setAttribute(
        'aria-label',
        (fermo ? this.dataset.labelPlay : this.dataset.labelPause) || ''
      );
      this.bottonePausa.setAttribute('aria-pressed', String(fermo));
    }

    clearInterval(this.timer);
    if (fermo || this.riduciMovimento?.matches) return;
    this.timer = setInterval(() => {
      // Solo dove scorre davvero: su schermo largo le fotografie sono colonne
      // affiancate e non c'e' niente da far avanzare.
      if (!this.hasAttribute('data-scorre')) return;
      const attuale = this.#indice();
      const meta = attuale + 1 >= this.items.length ? 0 : attuale + 1;
      // Si avanza solo quando sono arrivate sia la fotografia che si lascia
      // sia quella su cui si va. Su una rete da telefono il carosello partiva
      // dopo quattro secondi e scorreva su riquadri vuoti; e le diapositive
      // successive, piu' leggere, arrivavano prima della prima, che veniva
      // portata via un attimo prima di comparire (misurato).
      if (![attuale, meta].every((i) => this.#arrivata(this.items[i]))) return;
      this.#vaiA(meta);
    }, this.intervallo);
  }

  /**
   * Toglie il caricamento pigro alle immagini della striscia, al momento
   * giusto.
   *
   * Safari decide se caricare un'immagine `loading="lazy"` guardando lo
   * scorrimento VERTICALE della pagina: cio' che sta oltre il bordo destro di
   * uno scorritore orizzontale non entra mai nella finestra, e non viene mai
   * caricato. Il risultato e' un pannello vuoto dentro una sezione fatta di
   * sole fotografie.
   *
   * Prima le strisce erano `eager` nel markup, e partivano tutte insieme alla
   * fotografia in cima: su una rete da telefono quella arrivava per ultima, a
   * dieci secondi. Nemmeno `lazy` basta, perche' su rete lenta Chrome
   * anticipa le immagini pigre vicine. Le strisce e le diapositive
   * successive dell'apertura escono quindi dal server senza indirizzo
   * (`data-src`, `data-srcset`), e lo ricevono qui, a due condizioni: la
   * striscia e' a meno di una finestra di distanza, e l'immagine che la
   * pagina ha dichiarato principale (`fetchpriority="high"`) e' arrivata.
   *
   * Le immagini pigre aggiunte dall'editor, che un indirizzo ce l'hanno,
   * passano comunque a `eager`: e' il rimedio per Safari.
   */
  #sveglia() {
    const pigre = [
      ...this.track.querySelectorAll('img[loading="lazy"], img[data-src], img[data-srcset]'),
    ];
    if (!pigre.length) return;

    const sveglia = () =>
      this.#dopoLaPrincipale(() => {
        for (const img of pigre) this.#svegliaFoto(img);
      });

    if (typeof IntersectionObserver !== 'function') {
      sveglia();
      return;
    }
    this.vicino = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        this.vicino.disconnect();
        sveglia();
      },
      { rootMargin: '100% 0px' }
    );
    this.vicino.observe(this);
  }

  #svegliaFoto(img) {
    // `sizes="auto"` vale solo per le immagini pigre: passando a eager il
    // browser lo ignorerebbe e sceglierebbe il file per tutta la larghezza
    // dello schermo. Si scrive la larghezza vera, che e' cio' che `auto`
    // avrebbe calcolato.
    if ((img.getAttribute('sizes') || '').trim().startsWith('auto')) {
      const larghezza = Math.ceil(img.getBoundingClientRect().width);
      if (larghezza > 0) img.sizes = `${larghezza}px`;
    }
    img.loading = 'eager';
    // Prima `srcset` e poi `src`: al contrario il browser potrebbe partire
    // con `src`, che e' il file piu' grande, e poi cambiare idea.
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      delete img.dataset.srcset;
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      delete img.dataset.src;
    }
  }

  #dopoLaPrincipale(fatto) {
    const principale = document.querySelector('img[fetchpriority="high"]');
    if (!principale || principale.complete) {
      fatto();
      return;
    }
    // `error` conta come arrivata: una fotografia rotta non deve tenere
    // ferme tutte le altre.
    principale.addEventListener('load', fatto, { once: true });
    principale.addEventListener('error', fatto, { once: true });
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#programma);
    this.osservatore?.disconnect();
    this.vista?.disconnect();
    this.vicino?.disconnect();
    document.removeEventListener('visibilitychange', this.#visibilita);
    clearInterval(this.timer);
  }

  /** Un solo aggiornamento per fotogramma, anche con cento eventi di scroll. */
  #inCoda = false;
  #programma = () => {
    if (this.#inCoda) return;
    this.#inCoda = true;
    requestAnimationFrame(() => {
      this.#inCoda = false;
      this.#aggiorna();
    });
  };

  #costruisci() {
    const comandi = document.createElement('div');
    comandi.className = 'scroller__controls';

    this.precedente = this.#bottone('scroller__arrow scroller__arrow--prev', this.dataset.labelPrev);
    this.successivo = this.#bottone('scroller__arrow scroller__arrow--next', this.dataset.labelNext);
    this.precedente.addEventListener('click', () => this.#vaiA(this.#indice() - 1));
    this.successivo.addEventListener('click', () => this.#vaiA(this.#indice() + 1));

    this.pallini = document.createElement('div');
    this.pallini.className = 'scroller__dots';

    this.bottoniPallino = this.items.map((_, i) => {
      const b = this.#bottone('scroller__dot', (this.dataset.labelDot || '').replace('%d', i + 1));
      b.addEventListener('click', () => this.#vaiA(i));
      this.pallini.append(b);
      return b;
    });

    comandi.append(this.precedente, this.pallini, this.successivo);
    this.append(comandi);
  }

  #bottone(classe, etichetta) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = classe;
    // Il nome accessibile e' l'unica cosa che un bottone senza testo ha:
    // senza, uno screen reader annuncia soltanto "pulsante".
    b.setAttribute('aria-label', etichetta || '');
    return b;
  }

  /** Indice dell'immagine piu' vicina al bordo sinistro visibile. */
  #indice() {
    const x = this.track.scrollLeft;
    let vicino = 0;
    let minimo = Infinity;
    this.items.forEach((el, i) => {
      const d = Math.abs(el.offsetLeft - this.track.offsetLeft - x);
      if (d < minimo) {
        minimo = d;
        vicino = i;
      }
    });
    return vicino;
  }

  #vaiA(i) {
    const meta = this.items[Math.max(0, Math.min(this.items.length - 1, i))];
    if (!meta) return;
    const fermo = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: meta.offsetLeft - this.track.offsetLeft,
      behavior: fermo ? 'auto' : 'smooth',
    });
  }

  #aggiorna() {
    // Un margine di due pixel: gli arrotondamenti sub-pixel farebbero
    // comparire i comandi su strisce che in realta' non scorrono.
    const scorre = this.track.scrollWidth - this.track.clientWidth > 2;
    this.toggleAttribute('data-scorre', scorre);
    if (!scorre) return;

    const i = this.#indice();
    this.precedente.disabled = this.track.scrollLeft <= 2;
    this.successivo.disabled =
      this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;

    this.bottoniPallino.forEach((b, n) => {
      const attivo = n === i;
      b.toggleAttribute('data-attivo', attivo);
      // `aria-current` e non `aria-selected`: questi non sono schede, sono
      // scorciatoie a una posizione della striscia.
      if (attivo) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }
}

customElements.define('media-scroller', MediaScroller);
